/**
 * The live bell socket, browser side.
 *
 * Separate from the REST client on purpose: REST is authoritative and this is
 * an accelerator. If the socket never connects, the bell still works — it just
 * updates on the next poll or navigation instead of instantly. Nothing here may
 * throw into the caller for that reason.
 */
import { parseAuditChangedEvent, } from "@playbookai/types";
const BASE_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
/**
 * Reconnects with exponential backoff and full jitter.
 *
 * Jitter is not decoration here. Every open bell in the tenant loses its socket
 * at the same instant when a pod rolls, so a fixed backoff reconnects them all
 * in lockstep and the retry storm is the outage. Full jitter spreads them.
 */
export function reconnectDelay(attempt, random) {
    const ceiling = Math.min(MAX_RECONNECT_MS, BASE_RECONNECT_MS * 2 ** attempt);
    return Math.floor(random() * ceiling);
}
export function createBellSocket(options) {
    let socket = null;
    let timer = null;
    let stopped = false;
    let attempts = 0;
    /**
     * Set SYNCHRONOUSLY, before the first await in `open()`.
     *
     * The `socket` guard alone is not enough: minting a ticket is async, so two
     * calls arriving in the same tick both pass it and both go on to open a
     * socket. React StrictMode's double-invoked effect does precisely that, and
     * the result is two sockets per tab, two subscriptions, and every push
     * delivered twice.
     */
    let connecting = false;
    /**
     * Bumped by every `stop()`.
     *
     * `connecting` alone cannot survive a stop/start pair. `stop()` must clear
     * it — otherwise a later `start()` is blocked forever — but clearing it lets
     * an `open()` that is still awaiting `mintTicket()` resume into the NEW
     * lifecycle and assign a socket beside the one `start()` just opened. Two
     * sockets, two subscriptions, every push twice: exactly what the flag exists
     * to prevent.
     *
     * A generation expresses what a boolean cannot — "this attempt belongs to a
     * lifecycle that has since ended" — so the stale attempt discards itself.
     */
    let generation = 0;
    const factory = options.socketFactory ??
        ((url) => new WebSocket(url));
    function scheduleReconnect() {
        if (stopped || !options.url)
            return;
        const delay = reconnectDelay(attempts, Math.random);
        attempts += 1;
        timer = setTimeout(() => {
            timer = null;
            void open();
        }, delay);
    }
    async function open() {
        if (stopped || socket || connecting || !options.url)
            return;
        connecting = true;
        const mine = generation;
        let ticket;
        try {
            ticket = await options.mintTicket();
        }
        catch {
            // The BFF is unreachable or the session is gone. REST is authoritative,
            // so this degrades rather than surfacing.
            if (mine !== generation)
                return;
            connecting = false;
            scheduleReconnect();
            return;
        }
        // A stop() (and possibly a start()) happened while the ticket was in
        // flight. This attempt belongs to the previous lifecycle, so it discards
        // itself and leaves the new one alone.
        if (mine !== generation)
            return;
        if (!ticket || stopped) {
            connecting = false;
            scheduleReconnect();
            return;
        }
        const url = `${options.url}?ticket=${encodeURIComponent(ticket)}`;
        let next;
        try {
            next = factory(url);
        }
        catch {
            if (mine !== generation)
                return;
            connecting = false;
            scheduleReconnect();
            return;
        }
        if (mine !== generation) {
            // Opened into a dead lifecycle: close it rather than leaking the socket.
            next.close();
            return;
        }
        socket = next;
        connecting = false;
        next.addEventListener("open", () => {
            // Reset only on a SUCCESSFUL open. Resetting when the socket is created
            // would make the backoff useless: a server refusing every handshake would
            // be retried every second forever.
            attempts = 0;
        });
        next.addEventListener("message", (event) => {
            const push = parsePush(event.data);
            // A malformed frame is dropped, never thrown. The next REST read corrects
            // whatever it would have said.
            if (push) {
                options.onPush(push);
                return;
            }
            /* SECOND, and deliberately not a `type` discriminator inside `parsePush`:
               one socket now carries two unrelated vocabularies, and restructuring
               the parse path that every real bell push takes is the one thing this
               must not do. Only a frame `parsePush` ALREADY rejected reaches here, so
               bell keeps its exact previous behaviour and precedence.
      
               The two shapes are disjoint by construction, not by agreement: a bell
               push always has `unreadCount` + `latest` (so it can never fall
               through), and an audit nudge has neither (so it could never have been
               read as one, even before this branch existed). */
            const audit = parseAuditChangedEvent(event.data);
            if (audit)
                options.onAuditChanged?.(audit);
        });
        const onGone = () => {
            if (socket !== next)
                return;
            socket = null;
            scheduleReconnect();
        };
        next.addEventListener("close", onGone);
        next.addEventListener("error", onGone);
    }
    return {
        start() {
            if (!options.url)
                return;
            stopped = false;
            void open();
        },
        stop() {
            stopped = true;
            connecting = false;
            generation += 1;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            socket?.close();
            socket = null;
        },
        get attempts() {
            return attempts;
        },
    };
}
export function parsePush(data) {
    if (typeof data !== "string")
        return null;
    let raw;
    try {
        raw = JSON.parse(data);
    }
    catch {
        return null;
    }
    if (typeof raw !== "object" || raw === null)
        return null;
    const record = raw;
    const unreadCount = record["unreadCount"];
    const latest = record["latest"];
    if (typeof unreadCount !== "number" || !Number.isInteger(unreadCount)) {
        return null;
    }
    if (typeof latest !== "object" || latest === null)
        return null;
    const item = latest;
    if (typeof item["id"] !== "string" ||
        typeof item["title"] !== "string" ||
        typeof item["tone"] !== "string") {
        return null;
    }
    const href = item["href"];
    return {
        unreadCount,
        latest: {
            id: item["id"],
            title: item["title"],
            tone: item["tone"],
            href: typeof href === "string" ? href : null,
        },
    };
}
//# sourceMappingURL=bell-socket.js.map